import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../../lib/api";
import { statusPill } from "./AdminDashboard";
import AddRecordDialog from "../../components/AddRecordDialog";

const STATUSES = ["All", "New", "Contacted", "In Progress", "Pending", "Completed", "Rejected"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const params = {};
    if (status !== "All") params.status = status;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data.leads));
  }, [status, q, reload]);

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-leads-page">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="label-eyebrow">CRM</span>
          <h1 className="mt-2 text-3xl font-display font-medium tracking-tight text-[#121C17]">Leads <span className="text-[#5C6661] text-base ml-1">({leads.length})</span></h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, ref…" data-testid="leads-search"
              className="bg-white border border-[#E5E2DC] rounded-full pl-9 pr-4 py-2.5 text-sm focus:border-[#1E4D3B] w-full sm:w-64" />
          </div>
          <button onClick={() => setAddOpen(true)} data-testid="leads-add-button"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2">
            <Plus size={14} /> Add lead
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="leads-status-filter">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${
              status === s ? "bg-[#121C17] text-white border-[#121C17]" : "bg-white text-[#5C6661] border-[#E5E2DC] hover:border-[#121C17]"
            }`}>{s}</button>
        ))}
      </div>

      <div className="bento-card overflow-hidden" data-testid="leads-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-xs label-eyebrow border-b border-[#E5E2DC] bg-[#F3F1EC]">
          <span className="col-span-2">Ref</span>
          <span className="col-span-3">Name</span>
          <span className="col-span-3">Service</span>
          <span className="col-span-2">Mobile</span>
          <span className="col-span-2">Status</span>
        </div>
        {leads.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C6661]">No leads match these filters.</div>
        ) : (
          leads.map((l) => (
            <Link to={`/admin/leads/${l.id}`} key={l.id} data-testid={`lead-row-${l.id}`}
              className="block md:grid md:grid-cols-12 gap-3 px-4 sm:px-5 py-4 border-b border-[#E5E2DC] last:border-0 hover:bg-[#FDFCFB] transition-colors md:items-center">
              {/* Mobile card */}
              <div className="md:hidden">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[#121C17] truncate">{l.full_name}</span>
                  <span className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${statusPill(l.status)}`}>{l.status}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-[#5C6661]">
                  <span className="truncate">{l.service_name}</span>
                  <span className="font-mono shrink-0 ml-2">{l.ref}</span>
                </div>
                <div className="mt-0.5 text-xs text-[#5C6661]">📞 {l.mobile}</div>
              </div>
              {/* Desktop row */}
              <span className="hidden md:inline col-span-2 font-mono text-xs text-[#5C6661]">{l.ref}</span>
              <span className="hidden md:inline col-span-3 font-medium text-[#121C17]">{l.full_name}</span>
              <span className="hidden md:inline col-span-3 text-sm text-[#5C6661]">{l.service_name}</span>
              <span className="hidden md:inline col-span-2 text-sm text-[#5C6661]">{l.mobile}</span>
              <span className="hidden md:inline col-span-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${statusPill(l.status)}`}>{l.status}</span>
              </span>
            </Link>
          ))
        )}
      </div>

      <AddRecordDialog
        open={addOpen}
        kind="lead"
        onClose={() => setAddOpen(false)}
        onCreated={() => setReload((n) => n + 1)}
      />
    </div>
  );
}
